import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import {
  ProductCsvTemplate,
  FieldMapping,
} from '../../../../../types/csvTemplates'
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet'
import { MatDivider } from '@angular/material/divider'
import { MatListModule } from '@angular/material/list'
import { productCsvMappingFields } from '../../../../../helpers/product-csv-template-fields'
import { TranslationService } from '../../../../../services/translation.service'

type ProductFieldMapping = FieldMapping & { name: string }

@Component({
  selector: 'app-field-mapping-sheet',
  imports: [MatListModule, MatDivider],
  templateUrl: './product-field-mapping-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-field-mapping-sheet.component.scss',
})
export class ProductFieldMappingSheetComponent {
  data = inject<{ csvTemplate?: ProductCsvTemplate }>(MAT_BOTTOM_SHEET_DATA)
  translationService = inject(TranslationService)

  csvTemplate?: ProductCsvTemplate
  fields: Array<ProductFieldMapping> = []
  constructor() {
    const data = this.data

    if (data.csvTemplate) {
      this.csvTemplate = data.csvTemplate
      this.fields = this.mapCsvTemplateToFieldMappings(data.csvTemplate)
    }
  }

  /**
   * Handles the `keydown` event for keyboard navigation.
   * Moves focus between mapping rows with ArrowUp and ArrowDown.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered when a key is pressed down.
   * @param {number} index - The current index of the focused list item.
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    const listItems = document.querySelectorAll('a[mat-list-item]')

    if (event.key === 'ArrowDown') {
      // Prevent default scroll behavior
      event.preventDefault()

      // Focus the next item, if it exists
      if (index < listItems.length - 1)
        (listItems[index + 1] as HTMLElement).focus()
    } else if (event.key === 'ArrowUp') {
      // Prevent default scroll behavior
      event.preventDefault()

      // If the current index is greater than 0, focus on the previous item
      if (index > 0) (listItems[index - 1] as HTMLElement).focus()
    }
  }

  /**
   * Builds display rows for configured mappings, retaining stable field keys.
   * Includes each named custom column with its type and optional unit; omits blank mappings.
   * @param {ProductCsvTemplate} template Template whose mappings are displayed.
   * @returns {ProductFieldMapping[]} Configured fields with translated labels and aliases.
   */
  mapCsvTemplateToFieldMappings(
    template: ProductCsvTemplate,
  ): ProductFieldMapping[] {
    return productCsvMappingFields(this.translationService)
      .flatMap<ProductFieldMapping>((field) => {
        const alias = template[field.name]
        if (typeof alias !== 'string' || !alias.trim()) return []
        return [
          {
            name: field.name,
            systemField: field.label,
            alias,
          },
        ]
      })
      .concat(
        (template.customFields ?? []).map((field, index) => ({
          name: 'customFields.' + index,
          systemField: this.translationService.getCsvCustomFieldMappingText(
            field.name,
            field.type,
            field.unit,
          ),
          alias: field.column,
        })),
      )
  }
}
